/* The (old) AddressFormContainer with separate entry for each field */
import invariant from 'invariant';
import { useEffect, useRef, useState } from 'react';

import type { ApiAddress, ApiAddressWithFormData } from 'api/types/ApiAddress';
import { useDebouncedFn } from 'hooks/useDebouncedFn';
import { useI18n } from 'i18n/hooks/useI18n';
import type { Nullable } from 'types/Nullable';
import { setEquals } from 'utils/functional/array';
import { isEmpty } from 'utils/functional/poly';
import { useGMapsErrored } from 'zustand-stores/gMapsErrorStore';
import { showModal } from 'zustand-stores/modalStore';

import { AddressFormSeparate } from './AddressFormSeparate';
import { lookupAddress } from './lookupAddress';

const nowMs = () => new Date().getTime();

type Props = {
  showDescription?: boolean;
  canChangeLocationHidden?: boolean;
  address: ApiAddressWithFormData | null | undefined;
  updateAddress: (
    update: Nullable<Partial<ApiAddressWithFormData>>,
    callback?: () => void,
  ) => void;
  setFetchingAddress: (arg0: boolean) => void;
  clearErrors: () => void;
  error: string | null | undefined;
  // TODO: Fix this the next time the file is edited.
  // oxlint-disable-next-line typescript/no-explicit-any
  errors: Record<string, any>;
  disabled?: boolean;
  orgAddress?: ApiAddress;
  showLocationButtonText?: string;
  hideLocationButtonText?: string;
  index?: number;
  toggleSeparateFields: () => void;
  required?: boolean;
  namePrefix: string;
};

export function AddressFormSeparateContainerInternal({
  showDescription,
  canChangeLocationHidden,
  address,
  updateAddress,
  setFetchingAddress,
  clearErrors,
  error,
  errors,
  disabled = false,
  orgAddress,
  showLocationButtonText,
  hideLocationButtonText,
  index,
  toggleSeparateFields,
  required,
  namePrefix,
}: Props) {
  const { i18n } = useI18n();

  const { gMapsErrored } = useGMapsErrored();
  const [gMapsErrorModalShown, setGMapsErrorModalShown] = useState(false);
  useEffect(() => {
    const keys = [
      'line1',
      'line2',
      'city',
      'state',
      'stateCode',
      'zipCode',
      'full',
    ];
    const currentErrors = keys.filter((key) =>
      Object.keys(errors).includes(`${namePrefix}.${key}`),
    );

    if (setEquals(currentErrors, ['full'])) {
      toggleSeparateFields();
    }
  }, [errors, namePrefix, toggleSeparateFields]);
  // How this works:
  // Address updates via `updateAddress` are NOT done directly. Instead, they are
  // submitted to a central updater as a promise in `submitUpdate` with a `requestedAt` time.
  //
  // Note that since submitUpdate may itself be called asynchronously, it takes `requestedAt`
  // as an argument. `requestedAt` is intended to be the time at which the *user* requested the
  // update, so that later user changes are not overridden by earlier ones.
  //
  // `submitUpdate` adds the submission to a list and attempts to resolve the promise
  // along with some metadata. It tracks the list of all requests in `requests`. Note that it can be
  // passed an immediately resolved promise if no async operations are necessary to update the address.
  //
  // `handleResolvedAddress` then applies the result of request resolution IF there are no later requested
  // changes in the queue.
  //
  // For example, if the user makes a change to an autocomplete field, we initiate a request to fetch the lat/long.
  // If the user then selects an autocomplete suggestion, we use that suggestion since the initial request will
  // have an earlier time.
  //
  // `submitField` queues two updates, one to update the address field immediately with the data we have
  // and another (ensured to be applied later via setTimeout) with the results of a google map lookup. This is so that
  // a google maps lookup that finds nothing, as in the test data where we have New York, CA addresses, are applied as updates
  // via the first update.
  //
  const requests = useRef<{ requestedAt: number; request: Promise<unknown> }[]>(
    [],
  );

  const handleResolvedAddress = ([requestsRef, requestTime, addressUpdate]: [
    requestRef: typeof requests,
    requestedTime: number,
    addressUpdate: ApiAddressWithFormData,
  ]) => {
    const maxRequestTime = Math.max(
      ...requestsRef.current.map((x) => x.requestedAt),
    );

    if (maxRequestTime <= requestTime) {
      const update = addressUpdate.latLongAddress
        ? // TODO: Fix this the next time the file is edited.
          // oxlint-disable-next-line prefer-object-spread
          Object.assign({}, addressUpdate, {
            full: addressUpdate.latLongAddress,
          })
        : addressUpdate;
      updateAddress(update);
    }
  };

  const submitUpdate = (
    updatePromise: Promise<Partial<ApiAddressWithFormData>>,
    requestedAt: number,
  ) => {
    const r = Promise.all([
      Promise.resolve(requests),
      Promise.resolve(requestedAt),
      updatePromise,
    ]).then(handleResolvedAddress);

    requests.current = requests.current.concat([{ requestedAt, request: r }]);
  };

  const debouncedSubmitFieldUpdate = useDebouncedFn(
    (field: string, newValue: boolean | undefined, requestedAt: number) => {
      const update =
        field === 'locationHidden'
          ? {
              locationHidden: newValue,
              line1: '',
              line2: '',
              zipcode: '',
            }
          : {
              [field]: newValue,
            };

      const updatedAddress = { ...address, ...update };

      // Submit update for immediate execution with current time, submit lookup request for updating afterwards
      const lookupRequest = lookupAddress({
        currentAddress: updatedAddress,
        setFetchingAddress,
      });
      submitUpdate(Promise.resolve(update), requestedAt);
      setTimeout(() => submitUpdate(lookupRequest, requestedAt + 1), 0);
    },
    1000,
  );

  const submitFieldUpdate = (field: string, newValue: string) => {
    const requestedAt = nowMs();
    debouncedSubmitFieldUpdate(field, newValue, requestedAt);
  };

  const useOrgAddress = () => {
    invariant(orgAddress, 'Missing orgAddress');
    const requestedAt = nowMs();

    const lookupRequest = lookupAddress({
      currentAddress: orgAddress,
      setFetchingAddress,
    });

    submitUpdate(
      Promise.resolve({
        ...orgAddress,
        latLongAddress: null,
      } as unknown as Partial<ApiAddressWithFormData>),
      requestedAt,
    );

    setTimeout(() => submitUpdate(lookupRequest, requestedAt + 1), 0);
  };

  const overrideLatLong = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    updateAddress({
      latitude,
      longitude,
      latLongAddress: null,
    });
  };

  useEffect(() => {
    if (gMapsErrored && !gMapsErrorModalShown) {
      showModal('GENERIC_UNCLOSEABLE', {
        text: i18n(
          'Our geolocation provider (Google Maps) did not load correctly. Please reload the page.',
        ),
      });
      setGMapsErrorModalShown(true);
    }
  }, [i18n, gMapsErrored, gMapsErrorModalShown]);

  useEffect(() => {
    if (
      address &&
      isEmpty(address.latLongAddress) &&
      !address.latitude &&
      !address.longitude
    ) {
      const lookupRequest = lookupAddress({
        currentAddress: address,
        setFetchingAddress,
      });
      submitUpdate(lookupRequest, nowMs());
    } // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AddressFormSeparate
      clearErrors={clearErrors}
      submitFieldUpdate={submitFieldUpdate}
      address={address}
      index={index}
      showDescription={showDescription}
      canChangeLocationHidden={canChangeLocationHidden}
      overrideLatLong={overrideLatLong}
      error={error}
      disabled={disabled}
      useOrgAddress={orgAddress && useOrgAddress}
      showLocationButtonText={showLocationButtonText}
      hideLocationButtonText={hideLocationButtonText}
      required={required}
      toggleSeparateFields={toggleSeparateFields}
    />
  );
}
